import { useMemo } from "react";
import { Box, Button, Chip, Stack, Typography, useTheme } from "@mui/material";
import { Link as RouterLink } from "react-router";
import FrostedCard from "src/components/quote/FrostedCard";
import { amberCtaSx } from "src/components/quote/amberCtaSx";
import { staticMapUrl } from "src/utils/valut/staticMapUrl";
import { formatSentDate, statusLabel, statusChipColor } from "src/utils/valut/valutFormatters";
import { useLang, useT } from "src/i18n";
import KeikkaPumpIcon from "src/components/valut/KeikkaPumpIcon";
import { inferVehicleType } from "src/components/valut/inferVehicleType";
import { kayttokohdeOptions, labelFor } from "src/utils/quote/quoteConstants";

function describePump({ puomi, linjanPituus }, t) {
    if (puomi === 0) return t("valut.listCard.boomFromRear");
    const p = Number(puomi);
    if (Number.isFinite(p) && p > 0) return t("valut.listCard.boomSuffix", { m: p });
    const l = Number(linjanPituus);
    if (Number.isFinite(l) && l > 0) return t("valut.listCard.linjaSuffix", { m: l });
    return null;
}

export default function ValuListCard({ row, index = 0, hideStatusChip = false, hideOfferPill = false }) {
    const theme = useTheme();
    const { lang } = useLang();
    const t = useT();
    // Memoized: this card renders once per row in a list.
    const kkOptions = useMemo(() => kayttokohdeOptions(t), [t]);
    const w = theme.custom?.wizard ?? {};
    const easing = w.motion?.easing ?? "cubic-bezier(0.22, 1, 0.36, 1)";
    const { isPumi } = inferVehicleType({ m3: row.maaraM3, puomi: row.puomi });
    const url = staticMapUrl({ lat: row.lat, lng: row.lng, w: 240, h: 160 });
    const pumpDesc = describePump(row, t);

    return (
        <FrostedCard
            component={RouterLink}
            to={`/valut/${row.pumppuRequestId}`}
            sx={{
                p: 2,
                textDecoration: "none",
                color: "inherit",
                display: "block",
                transition: "transform 120ms ease",
                "&:hover": { transform: "translateY(-2px)" },
                // Rows rise in with a capped stagger on load / tab-change — sibling
                // stagger within one list, the legitimate case. Keyed by id, so on a
                // silent refetch only a genuinely new row animates. Transform-only:
                // composes with the hover lift and never gates a row invisible.
                animation: `bjValuIn 320ms ${easing} ${Math.min(index, 6) * 28}ms backwards`,
                "@keyframes bjValuIn": {
                    from: { transform: "translateY(8px)" },
                    to: { transform: "translateY(0)" },
                },
                "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
        >
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ alignItems: "stretch" }}>
                <Box
                    sx={{
                        position: "relative",
                        width: { xs: "100%", sm: 120 },
                        height: { xs: 140, sm: 80 },
                        flexShrink: 0,
                        borderRadius: w.fieldRadius ? `${w.fieldRadius}px` : "10px",
                        overflow: "hidden",
                        bgcolor: "rgba(244,242,238,0.04)",
                        backgroundImage: url ? `url(${url})` : "none",
                        backgroundSize: "cover",
                        backgroundPosition: "center",
                    }}
                    role="img"
                    aria-label={url ? t("valut.listCard.mapAlt", { address: row.formattedAddress || row.osoite }) : t("valut.listCard.mapAltUnavailable")}
                >
                    <Box
                        sx={{
                            position: "absolute",
                            bottom: { xs: 4, sm: 4 },
                            right: { xs: 4, sm: 4 },
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            pointerEvents: "none",
                            filter: "drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) drop-shadow(0 0 1px #fff) drop-shadow(0 1px 2px rgba(0,0,0,0.6))",
                        }}
                    >
                        <KeikkaPumpIcon
                            m3={row.maaraM3}
                            puomi={row.puomi}
                            linjanPituus={row.linjanPituus}
                            isPumi={isPumi}
                            size={48}
                            noAnimation
                            invertMode="light"
                        />
                    </Box>
                </Box>

                <Stack spacing={0.5} sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontFamily: "'Black Ops One', sans-serif", letterSpacing: 0.5 }}>
                        {row.formattedAddress || row.osoite}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                        {[labelFor(kkOptions, row.kayttokohde, row.kayttokohde), row.maaraM3 ? `${row.maaraM3} m³` : null, pumpDesc].filter(Boolean).join(" · ")}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ alignItems: "center", mt: 0.5, flexWrap: "wrap" }}>
                        <Typography variant="caption" sx={{ color: "text.secondary" }}>
                            {t("valut.listCard.sentPrefix", { date: formatSentDate(row.sentAt || row.createdAt, lang) })}
                        </Typography>
                        {!hideOfferPill && row.acceptedOfferProviderName && (
                            <Chip
                                size="small"
                                label={t("valut.listCard.acceptedPrefix", { provider: row.acceptedOfferProviderName })}
                                color="success"
                                sx={{ ml: 1 }}
                            />
                        )}
                        {!hideOfferPill && !row.acceptedOfferProviderName && row.offerCount > 0 && (
                            <Chip
                                size="small"
                                label={t("valut.listCard.offerCountLabel", { count: row.offerCount })}
                                sx={{ ml: 1, bgcolor: w.amber, color: "background.default", fontWeight: 600 }}
                            />
                        )}
                    </Stack>
                </Stack>

                <Stack sx={{ alignItems: { xs: "stretch", sm: "flex-end" }, justifyContent: "space-between" }}>
                    {!hideStatusChip && (
                        <Chip size="small" label={statusLabel(row.status, t)} color={statusChipColor(row.status)} />
                    )}
                    <Button
                        variant="contained"
                        sx={amberCtaSx}
                        component="span"
                    >
                        {t("valut.listCard.viewCta")}
                    </Button>
                </Stack>
            </Stack>
        </FrostedCard>
    );
}
